import React, { useCallback, useEffect, useState } from "react";
import { X, Eye, SlidersHorizontal, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchEyeShape,
  fetchEyeShapeCatalog,
  deleteEyeShape,
  saveEyeShape,
} from "../lib/api";
import { track } from "../lib/analytics";
import EyeShapeIcon from "./EyeShapeIcon";

/**
 * EyeShapeChip — affirming callout on the Routine page that surfaces
 * the eye shape the user picked in onboarding OR that GPT-4o Vision
 * detected on the last selfie. Tappable "Not quite right? Change it"
 * affordance opens a 6-option override panel.
 *
 * Privacy: only fetches GET /profile/eye-shape (anonymized to the
 * caller's own user). Forget button hits DELETE which clears all
 * three columns (eye_shape_enc, updated_at, source).
 */

const SHAPE_OPTIONS = [
  "hooded", "monolid", "almond", "round", "downturned", "upturned",
];

const EyeShapeChip = () => {
  const [shape, setShape] = useState(null);
  const [source, setSource] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [content, setContent] = useState(null);
  const [forgotten, setForgotten] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyShape = useCallback(
    (nextShape, nextSource, nextCatalog = catalog) => {
      if (!nextShape || nextShape === "unsure") {
        setShape(null);
        setContent(null);
        setSource(null);
        return;
      }
      setShape(nextShape);
      setSource(nextSource || null);
      const match = (nextCatalog || []).find((s) => s.id === nextShape);
      setContent(match || null);
    },
    [catalog],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profile, cat] = await Promise.all([
          fetchEyeShape().catch(() => ({ shape: null })),
          fetchEyeShapeCatalog().catch(() => ({ shapes: [] })),
        ]);
        if (cancelled) return;
        const nextCatalog = cat?.shapes || [];
        setCatalog(nextCatalog);
        if (profile?.shape) {
          applyShape(profile.shape, profile.source, nextCatalog);
        }
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onForget = async () => {
    try {
      await deleteEyeShape();
      track("eye_shape_forgotten", { shape });
    } catch {
      /* still hide the chip locally so it doesn't keep nagging */
    }
    setForgotten(true);
  };

  const onOverride = async (nextShape) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await saveEyeShape(nextShape, "user_set");
      track("eye_shape_overridden", { from: shape, to: nextShape });
      applyShape(res?.shape || nextShape, res?.source || "user_set");
      setOverrideOpen(false);
    } catch {
      /* leave panel open so user sees something happened */
    } finally {
      setSaving(false);
    }
  };

  if (!shape || !content || forgotten) return null;

  const showVisionTag = source === "vision";
  const showUserTag = source === "user_set";

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="relative bg-gradient-to-br from-[#FBF4F2] via-[#F4E5E1] to-[#EFD8D2] border border-[#E4D2DC] rounded-2xl p-5 pr-10 mb-6 overflow-hidden"
          data-testid="eye-shape-chip"
        >
          <div className="absolute -top-8 -right-8 h-28 w-28 rounded-full bg-[#9B6A6C]/15 blur-3xl pointer-events-none" />

          <div className="flex items-start gap-3 relative z-10">
            <div className="shrink-0 h-10 w-10 rounded-full bg-white/80 border border-[#E4D2DC] flex items-center justify-center">
              <EyeShapeIcon shape={shape} size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#9B6A6C]">
                  Your eye shape
                </span>
                {showUserTag && (
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] text-[#5D4050] bg-white/70 border border-[#E4D2DC] rounded-full px-2 py-0.5"
                    data-testid="eye-shape-chip-source-user"
                  >
                    You set this
                  </span>
                )}
                {showVisionTag && (
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] text-[#5D4050] bg-white/70 border border-[#E4D2DC] rounded-full px-2 py-0.5"
                    data-testid="eye-shape-chip-source-vision"
                  >
                    Detected
                  </span>
                )}
              </div>
              <h4 className="text-[#2C2123] font-medium leading-snug mb-1">
                Your eyes read as{" "}
                <span
                  className="italic font-serif text-[#5D4050]"
                  data-testid="eye-shape-chip-label"
                >
                  {content.label}
                </span>
                {" "}— {content.headline}.
              </h4>
              <p
                className="text-sm text-[#5C4B51] leading-relaxed mb-3"
                data-testid="eye-shape-chip-tip"
              >
                {content.tip}
              </p>
              <button
                type="button"
                onClick={() => setOverrideOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9B6A6C] hover:text-[#5D4050] transition-colors underline-offset-4 hover:underline"
                data-testid="eye-shape-chip-change"
              >
                <SlidersHorizontal className="h-3 w-3" />
                {showUserTag ? "Change my eye shape" : "Not quite right? Change it"}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {overrideOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden relative z-10"
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/60">
                  {SHAPE_OPTIONS.map((opt) => {
                    const c = catalog.find((s) => s.id === opt);
                    const isCurrent = shape === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        disabled={saving}
                        onClick={() => onOverride(opt)}
                        className={`text-left rounded-xl border p-2.5 flex items-center gap-2 transition-all bg-white/70 hover:bg-white ${
                          isCurrent
                            ? "border-[#9B6A6C] shadow-[0_4px_12px_-6px_rgba(155,106,108,0.5)]"
                            : "border-[#E4D2DC] hover:border-[#9B6A6C]"
                        }`}
                        data-testid={`eye-shape-chip-override-${opt}`}
                      >
                        <EyeShapeIcon shape={opt} size={36} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-medium text-[#2C2123] capitalize">
                              {c?.label || opt}
                            </span>
                            {isCurrent && (
                              <Check className="h-3 w-3 text-[#9B6A6C]" />
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="button"
            onClick={onForget}
            aria-label="Forget my saved eye shape"
            title="Forget this"
            className="absolute top-3 right-3 h-7 w-7 rounded-full text-[#9B6A6C] hover:bg-white/70 hover:text-[#5D4050] transition-colors flex items-center justify-center z-20"
            data-testid="eye-shape-chip-forget"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default EyeShapeChip;
